import { nip19 } from 'nostr-tools';
import { useParams } from 'react-router-dom';
import { ALL_VIDEO_KINDS } from '@/lib/videoTypes';
import { useVideoById } from '@/hooks/useVideoById';
import VideoPage, { VideoNotFound, VideoPageSkeleton } from './VideoPage';
import ProfilePage from './ProfilePage';
import NotFound from './NotFound';

function VideoById({ id }: { id: string }) {
  const query = useVideoById({ id });

  if (query.isLoading) return <VideoPageSkeleton />;
  if (!query.data) return <VideoNotFound />;
  return <VideoPage video={query.data} />;
}

function VideoByAddress({ kind, pubkey, identifier }: { kind: number; pubkey: string; identifier: string }) {
  const query = useVideoById({ kind, pubkey, identifier });

  if (query.isLoading) return <VideoPageSkeleton />;
  if (!query.data) return <VideoNotFound />;
  return <VideoPage video={query.data} />;
}

export function NIP19Page() {
  const { nip19: identifier } = useParams<{ nip19: string }>();

  if (!identifier) {
    return <NotFound />;
  }

  let decoded;
  try {
    decoded = nip19.decode(identifier);
  } catch {
    return <NotFound />;
  }

  const { type } = decoded;

  switch (type) {
    case 'npub':
      return <ProfilePage pubkey={decoded.data} />;

    case 'nprofile':
      return <ProfilePage pubkey={decoded.data.pubkey} />;

    case 'note':
      return <VideoById id={decoded.data} />;

    case 'nevent': {
      const { id, kind } = decoded.data;
      // Kind 1 is allowed: notes with linked video files are supported
      if (kind && kind !== 1 && !ALL_VIDEO_KINDS.includes(kind)) {
        return <NotFound />;
      }
      return <VideoById id={id} />;
    }

    case 'naddr': {
      const { kind, pubkey, identifier: dTag } = decoded.data;
      if (!ALL_VIDEO_KINDS.includes(kind)) {
        return <NotFound />;
      }
      return <VideoByAddress kind={kind} pubkey={pubkey} identifier={dTag} />;
    }

    default:
      return <NotFound />;
  }
}
